import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '@progress/kendo-angular-notification';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastContentComponent } from '@shared/components/toast-content/toast-content.component';
import {
  type ToastVariant,
  type ToastButtonAction,
  type ShowToastParams,
} from '@shared/components/toast-content/toast-types';

export type { ToastVariant, ToastButtonAction, ShowToastParams };

const DEFAULT_TOAST_DURATION = 5000;
// Kendo uses `hideAfter || defaultHideAfter` (not ??), so 0 is treated as
// "use default 5000ms". A large value is the only way to prevent auto-dismiss.
const PERSISTENT_TOAST_DURATION = 86_400_000;
const MAX_DUPLICATE_TOASTS = 3;

export interface ErrorDetails {
  message: string;
  status?: number;
  operation?: string;
  timestamp: Date;
  userId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalNotificationService {
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private activeToasts = new Map<string, number>();

  handleError(error: Error | HttpErrorResponse, operation?: string): void {
    const errorDetails = this.extractErrorDetails(error, operation);

    // Log error for debugging
    console.error('Global error:', errorDetails);

    // Handle specific error types
    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);
    } else {
      this.handleGeneralError(error);
    }

    // Show notification to user
    this.showErrorNotification(errorDetails);
  }

  private extractErrorDetails(
    error: Error | HttpErrorResponse,
    operation?: string,
  ): ErrorDetails {
    const timestamp = new Date();

    if (error instanceof HttpErrorResponse) {
      return {
        message: this.getHttpErrorMessage(error),
        status: error.status,
        operation,
        timestamp,
      };
    }

    return {
      message: error.message || 'An unexpected error occurred',
      operation,
      timestamp,
    };
  }

  private getHttpErrorMessage(error: HttpErrorResponse): string {
    // Try to extract meaningful message from server response
    if (error.error?.message) {
      return error.error.message;
    }

    // Fallback to status-based messages
    switch (error.status) {
      case 0:
        return 'Unable to connect to server. Please check your internet connection.';
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'You are not authorized to perform this action.';
      case 403:
        return 'You do not have permission to access this resource.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'There was a conflict with your request.';
      case 422:
        return 'The data provided is invalid.';
      case 500:
        return 'A server error occurred. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `An error occurred (${error.status}). Please try again.`;
    }
  }

  private handleHttpError(error: HttpErrorResponse): void {
    // Handle authentication errors
    if (error.status === 401) {
      console.warn('401 Unauthorized - redirecting to login');
      this.router.navigate(['/login']);
      return;
    }

    // Handle forbidden errors
    if (error.status === 403) {
      console.warn('403 Forbidden - access denied');
      return;
    }
  }

  private handleGeneralError(error: Error): void {
    // Handle general JavaScript errors
    console.error('Unhandled error:', error);
  }

  private showToast(
    variant: ToastVariant,
    title: string,
    message: string | undefined,
    buttonAction?: ToastButtonAction,
    duration?: number,
  ): void {
    const toastKey = `${variant}:${title}:${message ?? ''}`;
    const currentCount = this.activeToasts.get(toastKey) ?? 0;
    if (currentCount >= MAX_DUPLICATE_TOASTS) {
      return;
    }
    this.activeToasts.set(toastKey, currentCount + 1);

    const shouldAutoDismiss =
      variant === 'success' || variant === 'info' || variant === 'neutral';

    const hideAfter =
      duration ??
      (shouldAutoDismiss ? DEFAULT_TOAST_DURATION : PERSISTENT_TOAST_DURATION);

    const ref = this.notificationService.show({
      content: ToastContentComponent,
      type: { style: 'none', icon: false },
      closable: false,
      position: { horizontal: 'center', vertical: 'top' },
      animation: { type: 'fade', duration: 300 },
      cssClass: `app-toast app-toast-${variant}`,
      hideAfter,
    });

    const decrementCount = (): void => {
      const count = this.activeToasts.get(toastKey) ?? 0;
      if (count <= 1) {
        this.activeToasts.delete(toastKey);
      } else {
        this.activeToasts.set(toastKey, count - 1);
      }
    };

    if (ref.content) {
      const instance = ref.content.instance as ToastContentComponent;
      instance.title = title;
      instance.description = message ?? '';
      instance.variant = variant;
      instance.onClose = () => {
        decrementCount();
        ref.hide();
      };
      instance.buttonAction = buttonAction;
      ref.content.changeDetectorRef.detectChanges();
    }

    // Decrement when toast auto-dismisses
    setTimeout(() => decrementCount(), hideAfter);
  }

  private showErrorNotification(errorDetails: ErrorDetails): void {
    const message = errorDetails.operation
      ? `${errorDetails.operation}: ${errorDetails.message}`
      : errorDetails.message;

    this.showToast('error', 'Error', message);
  }

  show(params: ShowToastParams): void {
    this.showToast(
      params.type,
      params.title,
      params.message,
      params.buttonAction,
    );
  }

  showSuccess(message: string): void {
    this.showToast('success', 'Success', message);
  }

  showError(message: string): void {
    this.showToast('error', 'Error', message);
  }
}
